import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { FileText, Download, Eye, Calendar, User, Phone, Plus, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Invoice } from './Invoice';
import { getAdminDisplayName } from '../utils/adminNames';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const InvoicesPage = ({ user }) => {
  const adminName = getAdminDisplayName(user);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showCustomInvoiceForm, setShowCustomInvoiceForm] = useState(false);
  const [customInvoiceData, setCustomInvoiceData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: 'outstation',
    vehicleType: 'sedan',
    pickupLocation: '',
    dropLocation: '',
    date: '',
    time: '',
    notes: '',
    baseFare: '',
    extraCharges: '',
    gst: '5',
    toll: '',
    parking: '',
    // Outstation detailed breakdown
    totalRunning: '',
    bata: '',
    outstationToll: '',
    permit: '',
    advanceFuel: '',
    advanceAchal: '',
    discount: '',
    carModel: '',
    carRegistration: '',
    driverName: '',
    driverPhone: ''
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings`);
      const sorted = [...response.data].sort(
        (a, b) => new Date(a.created_at || a.date || 0) - new Date(b.created_at || b.date || 0)
      );
      setInvoices(sorted.map((inv, i) => ({ ...inv, sequence: i + 1 })).reverse());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
      setLoading(false);
    }
  };

  const generateInvoiceNumber = (booking) => {
    const date = new Date(booking.created_at || booking.date);
    const valid = !isNaN(date);
    const year = valid ? date.getFullYear() : new Date().getFullYear();
    const month = String(valid ? date.getMonth() + 1 : 1).padStart(2, '0');
    const day = String(valid ? date.getDate() : 1).padStart(2, '0');
    const sequence = String(booking.sequence || 1).padStart(3, '0');
    return `SGCAB-${year}${month}${day}-${sequence}`;
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice({ 
      ...invoice, 
      invoiceNumber: generateInvoiceNumber(invoice),
      createdBy: invoice.created_by || adminName
    });
    setShowInvoice(true);
  };

  const handleCloseInvoice = () => {
    setShowInvoice(false);
    setSelectedInvoice(null);
  };

  const handleCreateCustomInvoice = async () => {
    // Validate based on service type
    if (customInvoiceData.serviceType === 'airport') {
      if (!customInvoiceData.name || !customInvoiceData.phone || !customInvoiceData.baseFare) {
        toast.error('Please fill in required fields: Name, Phone, and Base Fare');
        return;
      }
    } else if (customInvoiceData.serviceType === 'outstation') {
      if (!customInvoiceData.name || !customInvoiceData.phone || !customInvoiceData.totalRunning) {
        toast.error('Please fill in required fields: Name, Phone, and Total Running');
        return;
      }
    } else {
      if (!customInvoiceData.name || !customInvoiceData.phone || !customInvoiceData.baseFare) {
        toast.error('Please fill in required fields: Name, Phone, and Base Fare');
        return;
      }
    }

    try {
      let total;
      let customBreakdown;
      
      if (customInvoiceData.serviceType === 'airport') {
        // Airport: Base fare + Toll + Parking - Discount
        const baseFare = parseFloat(customInvoiceData.baseFare);
        const toll = parseFloat(customInvoiceData.toll || 0);
        const parking = parseFloat(customInvoiceData.parking || 0);
        const discount = parseFloat(customInvoiceData.discount || 0);
        
        const subtotal = baseFare + toll + parking;
        total = subtotal - discount;
        customBreakdown = {
          base_fare: baseFare,
          toll: toll,
          parking: parking,
          discount: discount,
          subtotal: subtotal,
          total_amount: total,
          due_amount: total
        };
      } else if (customInvoiceData.serviceType === 'outstation') {
        // Outstation: Detailed breakdown
        const totalRunning = parseFloat(customInvoiceData.totalRunning || 0);
        const bata = parseFloat(customInvoiceData.bata || 0);
        const toll = parseFloat(customInvoiceData.outstationToll || 0);
        const permit = parseFloat(customInvoiceData.permit || 0);
        const advanceFuel = parseFloat(customInvoiceData.advanceFuel || 0);
        const advanceAchal = parseFloat(customInvoiceData.advanceAchal || 0);
        const discount = parseFloat(customInvoiceData.discount || 0);
        
        const subtotal = totalRunning + bata + toll + permit;
        const dueAmount = subtotal - advanceFuel - advanceAchal - discount;
        
        total = dueAmount;
        customBreakdown = {
          total_running: totalRunning,
          bata: bata,
          toll: toll,
          permit: permit,
          subtotal: subtotal,
          advance_fuel: advanceFuel,
          advance_achal: advanceAchal,
          discount: discount,
          due_amount: dueAmount,
          total_amount: subtotal
        };
      } else {
        // Rental/Corporate: Base fare + Extra charges + GST - Discount
        const baseFare = parseFloat(customInvoiceData.baseFare);
        const extraCharges = parseFloat(customInvoiceData.extraCharges || 0);
        const gstPercent = parseFloat(customInvoiceData.gst || 0);
        const discount = parseFloat(customInvoiceData.discount || 0);
        
        const subtotal = baseFare + extraCharges;
        const gstAmount = (subtotal * gstPercent) / 100;
        total = subtotal + gstAmount - discount;
        customBreakdown = {
          base_fare: baseFare,
          extra_charges: extraCharges,
          gst_percent: gstPercent,
          gst_amount: gstAmount,
          discount: discount,
          total_amount: total,
          due_amount: total
        };
      }

      const customInvoice = {
        name: customInvoiceData.name,
        phone: customInvoiceData.phone,
        email: customInvoiceData.email || null,
        serviceType: customInvoiceData.serviceType,
        vehicleType: customInvoiceData.vehicleType,
        pickupLocation: customInvoiceData.pickupLocation,
        dropLocation: customInvoiceData.dropLocation,
        date: customInvoiceData.date,
        time: customInvoiceData.time,
        notes: customInvoiceData.notes,
        custom_amount: total,
        custom_breakdown: customBreakdown,
        car_details: customInvoiceData.carModel ? {
          model: customInvoiceData.carModel,
          registration: customInvoiceData.carRegistration
        } : null,
        driver_details: customInvoiceData.driverName ? {
          name: customInvoiceData.driverName,
          phone: customInvoiceData.driverPhone
        } : null,
        created_by: adminName
      };

      const response = await axios.post(`${BACKEND_URL}/api/custom-invoices`, customInvoice);
      
      toast.success('Custom invoice created successfully!');
      setShowCustomInvoiceForm(false);
      fetchInvoices(); // Refresh the list
      
      // Reset form
      setCustomInvoiceData({
        name: '',
        phone: '',
        email: '',
        serviceType: 'outstation',
        vehicleType: 'sedan',
        pickupLocation: '',
        dropLocation: '',
        date: '',
        time: '',
        notes: '',
        baseFare: '',
        extraCharges: '',
        gst: '5',
        toll: '',
        parking: '',
        totalRunning: '',
        bata: '',
        outstationToll: '',
        permit: '',
        advanceFuel: '',
        advanceAchal: '',
        discount: '',
        carModel: '',
        carRegistration: '',
        driverName: '',
        driverPhone: ''
      });
    } catch (error) {
      console.error('Error creating custom invoice:', error);
      toast.error('Failed to create custom invoice');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date)) return 'N/A';
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Invoices</h2>
              <p className="text-gray-600">Generate and manage invoices for all bookings</p>
            </div>
            <button
              onClick={() => setShowCustomInvoiceForm(true)}
              className="inline-flex items-center px-6 py-3 bg-black hover:bg-gray-900 text-yellow-400 rounded-lg font-semibold transition-colors shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Custom Invoice
            </button>
          </div>
        </div>

        {/* Stats Card */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 mb-8 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium mb-1">Total Invoices</p>
              <p className="text-4xl font-bold">{invoices.length}</p>
            </div>
            <FileText className="w-16 h-16 text-blue-200 opacity-50" />
          </div>
        </div>

        {/* Invoices List */}
        {invoices.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No invoices available</p>
            <p className="text-gray-500 text-sm mt-2">Invoices will appear here as bookings are created</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {invoices.map((invoice) => (
              <Card key={invoice.id} className="hover:shadow-xl transition-shadow duration-300 border-2 border-gray-200 hover:border-blue-400">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg font-bold text-gray-900">
                        {generateInvoiceNumber(invoice)}
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        {formatDate(invoice.created_at)}
                      </p>
                    </div>
                    <Badge 
                      className={`${
                        invoice.status === 'confirmed' 
                          ? 'bg-green-100 text-green-700' 
                          : invoice.status === 'declined'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {invoice.status || 'Pending'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {/* Customer Info */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center text-sm">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900 font-medium">{invoice.name}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">{invoice.phone}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">
                        {formatDate(invoice.date)}{invoice.time ? ` at ${invoice.time}` : ''}
                      </span>
                    </div>
                  </div>

                  {/* Service Type */}
                  <div className="bg-blue-50 rounded-lg p-3 mb-4 border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium mb-1">Service</p>
                    <p className="text-sm font-semibold text-blue-900">
                      {invoice.serviceType} - {invoice.vehicleType}
                    </p>
                  </div>

                  {/* Amount Badge - if custom amount exists */}
                  {invoice.custom_amount && (
                    <div className="bg-green-50 rounded-lg p-3 mb-4 border border-green-200">
                      <p className="text-xs text-green-600 font-medium mb-1">Amount Due</p>
                      <p className="text-xl font-bold text-green-900">
                        ₹{invoice.custom_breakdown?.due_amount?.toLocaleString('en-IN') || invoice.custom_amount.toLocaleString('en-IN')}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <button
                    onClick={() => handleViewInvoice(invoice)}
                    className="w-full bg-black hover:bg-gray-900 text-yellow-400 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Invoice
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Invoice Modal */}
        {showInvoice && selectedInvoice && (
          <Invoice 
            booking={selectedInvoice} 
            invoiceNumber={selectedInvoice.invoiceNumber}
            onClose={handleCloseInvoice} 
          />
        )}

        {/* Custom Invoice Form Modal */}
        {showCustomInvoiceForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-gray-900">Create Custom Invoice</h2>
                <button
                  onClick={() => setShowCustomInvoiceForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Details */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Customer Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Name *
                      </label>
                      <input
                        type="text"
                        value={customInvoiceData.name}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter customer name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={customInvoiceData.phone}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email (Optional)
                      </label>
                      <input
                        type="email"
                        value={customInvoiceData.email}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter email"
                      />
                    </div>
                  </div>
                </div>

                {/* Service Details */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Service Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Type
                      </label>
                      <select
                        value={customInvoiceData.serviceType}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, serviceType: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="airport">Airport</option>
                        <option value="outstation">Outstation</option>
                        <option value="rental">Rental</option>
                        <option value="corporate">Corporate</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vehicle Type
                      </label>
                      <select
                        value={customInvoiceData.vehicleType}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, vehicleType: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="sedan">Sedan</option>
                        <option value="suv">SUV</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Location
                      </label>
                      <input
                        type="text"
                        value={customInvoiceData.pickupLocation}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, pickupLocation: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter pickup location"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Drop Location
                      </label>
                      <input
                        type="text"
                        value={customInvoiceData.dropLocation}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, dropLocation: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter drop location"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        value={customInvoiceData.date}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Time
                      </label>
                      <input
                        type="time"
                        value={customInvoiceData.time}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, time: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Pricing Details */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Pricing Details</h3>
                  
                  {customInvoiceData.serviceType === 'airport' ? (
                    // Airport: Base Fare + Toll + Parking
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Base Fare (₹) *
                        </label>
                        <input
                          type="number"
                          value={customInvoiceData.baseFare}
                          onChange={(e) => setCustomInvoiceData({...customInvoiceData, baseFare: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="1350 for Sedan, 1850 for SUV"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Toll Charges (₹)
                        </label>
                        <input
                          type="number"
                          value={customInvoiceData.toll}
                          onChange={(e) => setCustomInvoiceData({...customInvoiceData, toll: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Parking Charges (₹)
                        </label>
                        <input
                          type="number"
                          value={customInvoiceData.parking}
                          onChange={(e) => setCustomInvoiceData({...customInvoiceData, parking: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Discount (₹)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={customInvoiceData.discount}
                          onChange={(e) => setCustomInvoiceData({...customInvoiceData, discount: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                          data-testid="custom-invoice-discount-airport"
                        />
                        <p className="text-xs text-gray-500 mt-1">Optional — leave blank or 0 to hide on invoice</p>
                      </div>
                    </div>
                  ) : customInvoiceData.serviceType === 'outstation' ? (
                    // Outstation: Detailed breakdown
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-900 mb-3">Revenue Items</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Total Running (₹) *
                            </label>
                            <input
                              type="number"
                              value={customInvoiceData.totalRunning}
                              onChange={(e) => setCustomInvoiceData({...customInvoiceData, totalRunning: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                              placeholder="e.g., 18000"
                            />
                            <p className="text-xs text-gray-600 mt-1">Days × KM × Rate per KM</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Bata (₹)
                            </label>
                            <input
                              type="number"
                              value={customInvoiceData.bata}
                              onChange={(e) => setCustomInvoiceData({...customInvoiceData, bata: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                              placeholder="e.g., 1600"
                            />
                            <p className="text-xs text-gray-600 mt-1">Driver allowance per day</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Toll Charges (₹)
                            </label>
                            <input
                              type="number"
                              value={customInvoiceData.outstationToll}
                              onChange={(e) => setCustomInvoiceData({...customInvoiceData, outstationToll: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                              placeholder="e.g., 950"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Permit Charges (₹)
                            </label>
                            <input
                              type="number"
                              value={customInvoiceData.permit}
                              onChange={(e) => setCustomInvoiceData({...customInvoiceData, permit: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                              placeholder="e.g., 860"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="font-semibold text-red-900 mb-3">Deductions</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Advance - Fuel (₹)
                            </label>
                            <input
                              type="number"
                              value={customInvoiceData.advanceFuel}
                              onChange={(e) => setCustomInvoiceData({...customInvoiceData, advanceFuel: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                              placeholder="e.g., 5089"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Advance - Achal (₹)
                            </label>
                            <input
                              type="number"
                              value={customInvoiceData.advanceAchal}
                              onChange={(e) => setCustomInvoiceData({...customInvoiceData, advanceAchal: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                              placeholder="e.g., 500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Discount (₹)
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={customInvoiceData.discount}
                              onChange={(e) => setCustomInvoiceData({...customInvoiceData, discount: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                              placeholder="0"
                              data-testid="custom-invoice-discount-outstation"
                            />
                            <p className="text-xs text-gray-500 mt-1">Optional — leave blank or 0 to hide on invoice</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Rental/Corporate: Base Fare + Extra Charges + GST + Discount
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Base Fare (₹) *
                        </label>
                        <input
                          type="number"
                          value={customInvoiceData.baseFare}
                          onChange={(e) => setCustomInvoiceData({...customInvoiceData, baseFare: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter base fare"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Extra Charges (₹)
                        </label>
                        <input
                          type="number"
                          value={customInvoiceData.extraCharges}
                          onChange={(e) => setCustomInvoiceData({...customInvoiceData, extraCharges: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GST (%)
                        </label>
                        <input
                          type="number"
                          value={customInvoiceData.gst}
                          onChange={(e) => setCustomInvoiceData({...customInvoiceData, gst: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="5"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Discount (₹)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={customInvoiceData.discount}
                          onChange={(e) => setCustomInvoiceData({...customInvoiceData, discount: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                          data-testid="custom-invoice-discount-rental"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Car & Driver Details */}
                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Car & Driver Details (Optional)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Car Model
                      </label>
                      <input
                        type="text"
                        value={customInvoiceData.carModel}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, carModel: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Toyota Innova"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Registration Number
                      </label>
                      <input
                        type="text"
                        value={customInvoiceData.carRegistration}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, carRegistration: e.target.value.toUpperCase()})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 uppercase"
                        placeholder="KA-01-AB-1234"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Driver Name
                      </label>
                      <input
                        type="text"
                        value={customInvoiceData.driverName}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, driverName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter driver name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Driver Phone
                      </label>
                      <input
                        type="tel"
                        value={customInvoiceData.driverPhone}
                        onChange={(e) => setCustomInvoiceData({...customInvoiceData, driverPhone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter driver phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes
                  </label>
                  <textarea
                    value={customInvoiceData.notes}
                    onChange={(e) => setCustomInvoiceData({...customInvoiceData, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCustomInvoiceForm(false)}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCustomInvoice}
                  className="flex-1 px-6 py-3 bg-black hover:bg-gray-900 text-yellow-400 rounded-lg font-semibold transition-colors"
                >
                  Create Invoice
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
